import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	NavBreadcrumb,
	PageRequest,
	ServerDataResponse,
	TemplatesWithOptions
} from '@models/models';
import {
	QtTemplateListItem,
	QtTemplatePayload,
	QtTemplateType,
	QuoteIntroduction
} from '@models/qt-templates.model';
import { Subject } from 'rxjs/Subject';
import { filter, takeUntil } from 'rxjs/operators';
import { MessageService } from '@services/helpers/message-service/message.service';
import { AccountServiceWebProvider } from '@web-services/user/account.service';
import { Observable } from 'rxjs/Observable';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { QuoteInvoiceTemplatesDataService } from '@services/quote-invoice-templates/quote-invoice-templates.data.service';

@Component({
	selector: 'qt-templates',
	template: require('./qt-templates.component.html'),
	styles: [require('./qt-templates.component.scss')]
})
export class QtTemplatesComponent implements OnInit, OnDestroy {
	readonly breadcrumbs: Array<NavBreadcrumb> = [
		{ link: '/', title: 'Dashboard' },
		{ link: '/settings', title: 'Settings' },
		{ link: null, title: 'Quote & Invoice Templates' }
	];

	customQuote: QtTemplatePayload;
	quote: QtTemplatePayload;
	invoice: QtTemplatePayload;

	fixedQuoteIntroduction: QuoteIntroduction;
	pickAndChooseIntroduction: QuoteIntroduction;

	private companyId: number;
	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private qtTemplatesDataService: QuoteInvoiceTemplatesDataService,
		private accountService: AccountServiceWebProvider,
		private messageService: MessageService
	) {}

	ngOnInit(): void {
		this.getInitialData();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	loadNewTemplateData([pageRequest, type]: [
		PageRequest,
		QtTemplateType
	]): void {
		const sectionName = this.nameConverter(type.toLowerCase());
		this.loadTemplateSection([pageRequest, sectionName]).subscribe(
			({ data }): TemplatesWithOptions<QtTemplateListItem> =>
				(this[sectionName] = JSON.parse(JSON.stringify(data))),
			(error): void => this.messageService.handleError(error)
		);
	}

	private loadTemplateSection([pageRequest, sectionName]: [
		PageRequest,
		string
	]): Observable<ServerDataResponse<QtTemplatePayload>> {
		const methodName = `get${this.titleCase(sectionName)}TemplatesList`;
		return this.qtTemplatesDataService[methodName](pageRequest).pipe(
			takeUntil(this.unsubscribe),
			filter(
				(
					data: ServerDataResponse<TemplatesWithOptions<QtTemplateListItem>>
				): boolean => !!data
			)
		);
	}

	private loadPageTemplates(): void {
		const types: Array<QtTemplateType> = ['CUSTOM_QUOTE', 'QUOTE', 'INVOICE'];

		combineLatest(
			this.loadTemplateSection([
				this.getPageOptions(types[0]),
				this.nameConverter(types[0].toLowerCase())
			]),
			this.loadTemplateSection([
				this.getPageOptions(types[1]),
				this.nameConverter(types[1].toLowerCase())
			]),
			this.loadTemplateSection([
				this.getPageOptions(types[2]),
				this.nameConverter(types[2].toLowerCase())
			]).pipe(takeUntil(this.unsubscribe))
		).subscribe(
			(payload): void =>
				payload.forEach(({ data }, index: number): void => {
					const sectionName = this.nameConverter(types[index].toLowerCase());
					this[sectionName] = JSON.parse(JSON.stringify(data));
				}),
			(error): void => this.messageService.handleError(error)
		);
	}

	private loadIntroduction(): void {
		this.qtTemplatesDataService
			.getQuoteIntroduction()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				({ data }): void => {
					this.fixedQuoteIntroduction = data.fixedIntroduction;
					this.pickAndChooseIntroduction = data.chooseAndPickIntroduction;
				},
				(error): void => this.messageService.handleError(error)
			);
	}

	private getInitialData(): void {
		this.accountService.companyIdObservable
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((data: number): void => {
				this.companyId = data;
				this.loadPageTemplates();
				this.loadIntroduction();
			});
	}

	private getPageOptions(type: QtTemplateType): PageRequest {
		const localStorageField = `${type.toLowerCase()}_templates_request_${
			this.companyId
		}`;
		const pageOptions = localStorage.getItem(localStorageField);

		const options = {
			paging: {
				pageNumber: 0,
				pageSize: 10,
				searchToken: null
			},
			sortCriterias: [
				{
					fieldName: 'name',
					sortAscending: true
				}
			]
		};

		if (pageOptions) {
			const savedOptions = JSON.parse(pageOptions);
			options.paging.pageSize = savedOptions.pageSize;
			options.sortCriterias[0].fieldName = savedOptions.fieldName;
			options.sortCriterias[0].sortAscending = savedOptions.sortAscending;
		}

		return options;
	}

	private nameConverter(dashedName: string): string {
		return dashedName.replace(/_([a-z])/g, ([, g]: string): string =>
			g.toUpperCase()
		);
	}

	private titleCase(name: string): string {
		return name
			.split('')
			.map((e: string, i: null): string => (i === 0 ? e.toUpperCase() : e))
			.join('');
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/settings/quote-invoice-templates/qt-templates/qt-templates.component.ts